/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useRiskCalculator - Hook para gestión de Risk Meter
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file useRiskCalculator.ts
 * @version 1.0.0
 * @description Hook React para calcular y gestionar el estado del Risk Meter
 * 
 * FEATURES:
 * - Cálculo automático basado en inputs
 * - Persistencia en localStorage
 * - Historial de últimos 20 scores
 * - Actualización en tiempo real
 * - Recomendaciones priorizadas
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { WhatsAppRiskAssessment } from '../types/bulksend.types';
import { 
  calculateRiskAssessment, 
  countEmojis, 
  countLinks, 
  countVariables 
} from '../types/riskAlgorithm';

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════════════

interface RiskInputs {
  message: string;
  contacts: any[];
  variables?: any[];
  scheduledHour?: number;
  scheduledDay?: string;
  sendingDays?: string[];
  firstResponseEnabled?: boolean;
  envConfigured?: boolean;
  scheduleConfigured?: boolean;
}

interface RiskHistoryItem {
  timestamp: number;
  score: number;
  level: string;
  flagProbability: number;
}

interface UseRiskCalculatorReturn {
  // Estado actual
  riskAssessment: WhatsAppRiskAssessment | null;
  isCalculating: boolean;
  
  // Historial
  history: RiskHistoryItem[];
  trend: 'up' | 'down' | 'stable' | 'neutral';
  
  // Acciones
  calculateRisk: (inputs: RiskInputs) => void;
  recalculate: () => void;
  clearHistory: () => void;
  
  // Utilidades
  getScoreTrend: () => 'up' | 'down' | 'stable' | 'neutral';
  getAverageScore: () => number;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const STORAGE_KEYS = {
  CURRENT_RISK: 'bulkSend.riskMeter.current',
  HISTORY: 'bulkSend.riskMeter.history',
  LAST_INPUTS: 'bulkSend.riskMeter.lastInputs'
};

const MAX_HISTORY_ITEMS = 20;

// ═══════════════════════════════════════════════════════════════════════════
// HOOK PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

export function useRiskCalculator(initialInputs?: RiskInputs): UseRiskCalculatorReturn {
  // ─────────────────────────────────────────────────────────────────────────
  // ESTADO
  // ─────────────────────────────────────────────────────────────────────────
  
  const [riskAssessment, setRiskAssessment] = useState<WhatsAppRiskAssessment | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [history, setHistory] = useState<RiskHistoryItem[]>([]);
  const [lastInputs, setLastInputs] = useState<RiskInputs | null>(initialInputs || null);
  
  // ─────────────────────────────────────────────────────────────────────────
  // CARGAR DESDE LOCALSTORAGE AL MONTAR
  // ─────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    try {
      // Cargar risk actual
      const savedRisk = localStorage.getItem(STORAGE_KEYS.CURRENT_RISK);
      if (savedRisk) {
        setRiskAssessment(JSON.parse(savedRisk));
      }
      
      // Cargar historial
      const savedHistory = localStorage.getItem(STORAGE_KEYS.HISTORY);
      if (savedHistory) {
        setHistory(JSON.parse(savedHistory));
      }
      
      // Cargar últimos inputs
      const savedInputs = localStorage.getItem(STORAGE_KEYS.LAST_INPUTS);
      if (savedInputs && !initialInputs) {
        setLastInputs(JSON.parse(savedInputs));
      }
    } catch (error) {
      console.error('[useRiskCalculator] Error loading from localStorage:', error);
    }
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // FUNCIÓN PRINCIPAL: CALCULAR RIESGO
  // ─────────────────────────────────────────────────────────────────────────
  
  const calculateRisk = useCallback((inputs: RiskInputs) => {
    setIsCalculating(true);
    
    try {
      const {
        message,
        contacts,
        variables = [],
        scheduledHour,
        scheduledDay,
        sendingDays = [],
        firstResponseEnabled = false,
        envConfigured = false,
        scheduleConfigured = false
      } = inputs;
      
      // Extraer métricas del mensaje
      const messageLength = message.length;
      const emojiCount = countEmojis(message);
      const linkCount = countLinks(message);
      const variableCount = countVariables(message);
      
      // Calcular lista y rate
      const listSize = contacts.length;
      const estimatedHours = Math.ceil(listSize / 50); // 50 msg/hora rate limit
      
      // Llamar al algoritmo
      const assessment = calculateRiskAssessment({
        totalContacts: listSize,
        estimatedHours,
        listSize,
        messageLength,
        emojiCount,
        linkCount,
        variableCount,
        hasPersonalization: variableCount > 0 || variables.length > 0,
        scheduledHour,
        scheduledDay,
        sendingDays,
        firstResponseEnabled,
        envConfigured,
        scheduleConfigured
      });
      
      // Guardar resultado
      setRiskAssessment(assessment);
      setLastInputs(inputs);
      
      // Guardar en localStorage
      localStorage.setItem(STORAGE_KEYS.CURRENT_RISK, JSON.stringify(assessment));
      localStorage.setItem(STORAGE_KEYS.LAST_INPUTS, JSON.stringify(inputs));
      
      // Agregar al historial
      addToHistory({
        timestamp: Date.now(),
        score: assessment.overallScore,
        level: assessment.riskLevel,
        flagProbability: assessment.flagProbability
      });
      
    } catch (error) {
      console.error('[useRiskCalculator] Error calculating risk:', error);
    } finally {
      setIsCalculating(false);
    }
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // RECALCULAR CON ÚLTIMOS INPUTS
  // ─────────────────────────────────────────────────────────────────────────
  
  const recalculate = useCallback(() => {
    if (lastInputs) {
      calculateRisk(lastInputs);
    }
  }, [lastInputs, calculateRisk]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // GESTIÓN DE HISTORIAL
  // ─────────────────────────────────────────────────────────────────────────
  
  const addToHistory = useCallback((item: RiskHistoryItem) => {
    setHistory(prevHistory => {
      const newHistory = [item, ...prevHistory].slice(0, MAX_HISTORY_ITEMS);
      localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);
  
  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(STORAGE_KEYS.HISTORY);
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // UTILIDADES: TENDENCIA
  // ─────────────────────────────────────────────────────────────────────────
  
  const getScoreTrend = useCallback((): 'up' | 'down' | 'stable' | 'neutral' => {
    if (history.length < 2) return 'neutral';
    
    const recent = history.slice(0, 3);
    const older = history.slice(3, 6);
    
    if (recent.length === 0 || older.length === 0) return 'stable';
    
    const recentAvg = recent.reduce((sum, item) => sum + item.score, 0) / recent.length;
    const olderAvg = older.reduce((sum, item) => sum + item.score, 0) / older.length;
    
    const diff = recentAvg - olderAvg;
    
    if (diff > 5) return 'up';
    if (diff < -5) return 'down';
    return 'stable';
  }, [history]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // UTILIDADES: PROMEDIO
  // ─────────────────────────────────────────────────────────────────────────
  
  const getAverageScore = useCallback((): number => {
    if (history.length === 0) return 0;
    
    const sum = history.reduce((acc, item) => acc + item.score, 0);
    return Math.round(sum / history.length);
  }, [history]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // AUTO-CALCULAR SI HAY INPUTS INICIALES
  // ─────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    if (initialInputs && !riskAssessment) {
      calculateRisk(initialInputs);
    }
  }, [initialInputs]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // RETORNO
  // ─────────────────────────────────────────────────────────────────────────
  
  return {
    riskAssessment,
    isCalculating,
    history,
    trend: getScoreTrend(),
    calculateRisk,
    recalculate,
    clearHistory,
    getScoreTrend,
    getAverageScore
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK AUXILIAR: MONITOREO EN TIEMPO REAL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook que monitorea cambios en inputs y recalcula automáticamente
 */
export function useAutoRiskCalculation(inputs: RiskInputs, debounceMs: number = 500) {
  const { calculateRisk, riskAssessment } = useRiskCalculator();
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Limpiar timeout anterior
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    // Crear nuevo timeout
    const newTimeoutId = setTimeout(() => {
      calculateRisk(inputs);
    }, debounceMs);
    
    setTimeoutId(newTimeoutId);
    
    // Cleanup
    return () => {
      if (newTimeoutId) {
        clearTimeout(newTimeoutId);
      }
    };
  }, [
    inputs.message,
    inputs.contacts.length,
    inputs.scheduledHour,
    inputs.scheduledDay,
    inputs.firstResponseEnabled
  ]);
  
  return riskAssessment;
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTACIONES
// ═══════════════════════════════════════════════════════════════════════════

export default useRiskCalculator;
